import type { AxiosRequestConfig } from "axios";

import { track } from "@/utils/autotrack";

export type ActionTrackPhase = "success" | "failure";

export type ActionTrackConfig = {
  readonly event: string;
  readonly section?: string;
  readonly props?: Record<string, unknown>;
  readonly trackSuccess?: boolean;
  readonly trackFailure?: boolean;
};

const GENERIC_MUTATION_EVENT = "api.mutation";

function normalizeMethod(method: string | undefined): string {
  return (method ?? "GET").toUpperCase();
}

function isMutationMethod(method: string): boolean {
  return (
    method === "POST" ||
    method === "PUT" ||
    method === "PATCH" ||
    method === "DELETE"
  );
}

function buildProps(
  config: AxiosRequestConfig,
  phase: ActionTrackPhase,
  status?: number
): Record<string, unknown> {
  return {
    ...config.actionTrack?.props,
    phase,
    status,
    method: normalizeMethod(config.method),
    path: config.url,
  };
}

export function trackHttpAction(
  config: AxiosRequestConfig | undefined,
  phase: ActionTrackPhase,
  status?: number
): void {
  if (!config) return;

  const actionTrack = config.actionTrack;
  const method = normalizeMethod(config.method);

  if (!actionTrack && !isMutationMethod(method)) return;
  if (actionTrack?.trackSuccess === false && phase === "success") return;
  if (actionTrack?.trackFailure === false && phase === "failure") return;

  track(actionTrack?.event ?? GENERIC_MUTATION_EVENT, {
    section: actionTrack?.section,
    props: {
      ...buildProps(config, phase, status),
      tracked_by: actionTrack ? "actionTrack" : "axios_mutation",
    },
  });
}

export async function runTrackedAction<T>(
  actionTrack: ActionTrackConfig,
  run: () => Promise<T>
): Promise<T> {
  try {
    const result = await run();
    track(actionTrack.event, {
      section: actionTrack.section,
      props: {
        ...actionTrack.props,
        phase: "success",
      },
    });
    return result;
  } catch (error) {
    track(actionTrack.event, {
      section: actionTrack.section,
      props: {
        ...actionTrack.props,
        phase: "failure",
        error:
          error instanceof Error ? error.message : "unknown action error",
      },
    });
    throw error;
  }
}
